import { useRef, type PointerEvent } from 'react';
import { getGhostPiece } from '../game/engine';
import { getPieceCells } from '../game/tetrominoes';
import { BOARD_HEIGHT, BOARD_WIDTH, type GameState, type TetrominoType } from '../game/types';
import type { GameControls } from '../hooks/useGame';

interface RenderCell {
  type: TetrominoType | null;
  active: boolean;
  ghost: boolean;
}

interface GameBoardProps {
  game: GameState;
  controls: GameControls;
  showGhost?: boolean;
}

export function GameBoard({ game, controls, showGhost = true }: GameBoardProps) {
  const pointerStart = useRef<{ x: number; y: number } | null>(null);
  const rendered: RenderCell[][] = game.board.map((row) => row.map((type) => ({ type, active: false, ghost: false })));
  const ghost = getGhostPiece(game);

  if (ghost && showGhost) {
    for (const { x, y } of getPieceCells(ghost)) {
      if (y >= 0 && y < BOARD_HEIGHT && x >= 0 && x < BOARD_WIDTH && !rendered[y][x].type) {
        rendered[y][x] = { type: ghost.type, active: false, ghost: true };
      }
    }
  }

  if (game.active) {
    for (const { x, y } of getPieceCells(game.active)) {
      if (y >= 0 && y < BOARD_HEIGHT && x >= 0 && x < BOARD_WIDTH) {
        rendered[y][x] = { type: game.active.type, active: true, ghost: false };
      }
    }
  }

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (game.status !== 'playing') return;
    pointerStart.current = { x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const start = pointerStart.current;
    pointerStart.current = null;
    if (!start || game.status !== 'playing') return;
    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;
    const distance = Math.hypot(deltaX, deltaY);

    if (distance < 14) {
      controls.rotateClockwise();
    } else if (Math.abs(deltaX) > Math.abs(deltaY)) {
      const moves = Math.min(4, Math.max(1, Math.round(Math.abs(deltaX) / 32)));
      for (let index = 0; index < moves; index += 1) {
        if (deltaX < 0) controls.moveLeft();
        else controls.moveRight();
      }
    } else if (deltaY > 0) {
      const drops = Math.min(6, Math.max(1, Math.round(deltaY / 28)));
      for (let index = 0; index < drops; index += 1) controls.softDrop();
    } else {
      controls.rotateClockwise();
    }
  };

  const clearingRows = new Set(game.lastEvent?.kind === 'lineClear' ? game.lastEvent.clearedRows : []);
  const eventKey = game.lastEvent?.id ?? 0;

  return (
    <div
      className={`board-frame${game.lastEvent?.kind === 'hardDrop' ? ' board-frame--impact' : ''}`}
      key={eventKey}
      onPointerDown={onPointerDown}
      onPointerMove={(event) => event.preventDefault()}
      onPointerUp={onPointerUp}
      onPointerCancel={() => { pointerStart.current = null; }}
    >
      <div
        className="game-board"
        role="img"
        aria-label={`Game board. ${game.status}. Level ${game.level}, score ${game.score}, ${game.lines} lines cleared.`}
      >
        {rendered.flatMap((row, y) => row.map((cell, x) => {
          const classes = [
            'board-cell',
            cell.type ? `piece-${cell.type.toLowerCase()}` : '',
            cell.active ? 'board-cell--active' : '',
            cell.ghost ? 'board-cell--ghost' : '',
            clearingRows.has(y) ? 'board-cell--clearing' : '',
          ].filter(Boolean).join(' ');
          return <span key={`${x}:${y}`} className={classes} aria-hidden="true" />;
        }))}
      </div>
      <span className="board-corner board-corner--tl" aria-hidden="true" />
      <span className="board-corner board-corner--br" aria-hidden="true" />
    </div>
  );
}
